import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del } from '@/shared/api/api-client';
import { doctorWorkingHoursListQueryKey } from './use-doctor-working-hours-list';
import { doctorWorkingHourByIdQueryKey } from './use-doctor-working-hour-by-id';

/**
 * Soft-delete a doctor working-hours profile (PROD-762 PR-D).
 *   DELETE /api/Doctor/DeleteWorkingHours/{workingHourId}
 *
 * Flutter parity: `deleteDoctorWorkingHours` in
 * `settings_data_source_impl.dart:1045`. Backend archives the row
 * (sets `isDeleted: true`) — it doesn't hard-delete.
 *
 * Wire-shape notes:
 *   - URL uses PLURAL `DeleteWorkingHours/{id}` (matches Flutter
 *     verbatim — do NOT singularise it).
 *   - No request body.
 *
 * Invalidates the list cache so the Schedule tab drops the row. Also
 * removes the by-id slot from cache so a stale editor can't re-open
 * an archived record.
 */
export function useDeleteDoctorWorkingHours(doctorSetupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workingHourId: string) => {
      if (!workingHourId) {
        throw new Error(
          'useDeleteDoctorWorkingHours requires a non-empty workingHourId',
        );
      }
      return del<unknown>(`/api/Doctor/DeleteWorkingHours/${workingHourId}`);
    },
    onSuccess: (_data, workingHourId) => {
      queryClient.invalidateQueries({
        queryKey: doctorWorkingHoursListQueryKey(doctorSetupId),
      });
      queryClient.removeQueries({
        queryKey: doctorWorkingHourByIdQueryKey(workingHourId),
      });
    },
  });
}
